import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Checkbox, Icon } from "semantic-ui-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
// Components
import { Button } from "../atoms/Button";
// Actions
import { fetchIntegrations, saveIntegration, verifyIntegration } from "../../redux/actions/IntegrationActions";
// Constants
import { IntegrationType } from "../../constants/types";
// Themes
import "./PageStyles.scss";
import "../organisms/OrganismStyles.scss";

/**
 * Operator-facing configuration for third-party integrations. Each integration
 * is rendered as its own self-contained card so it's clear which settings
 * belong to which provider, and more cards can be added in the future.
 *
 * All providers' config is fetched in a single `GET /integrations` call (plus
 * the operator's subscribed zips) so adding another card doesn't mean another
 * request — each card receives its slice of that data as props and only talks
 * to its own provider endpoint when the operator saves.
 *
 * Shipday: connect an API key, then (once connected) choose which subscribed
 * zips Shipday covers. Donations created in those zips are sent to Shipday as a
 * delivery order.
 */
export default function Integrations() {
  const dispatch = useDispatch();
  const { data: integrations,  fetched, subscribedZips, busy } = useSelector(state => state.integration);

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, []);

  if (busy && !fetched) {
    return (
      <div className="main_container_section">
        <div className="loading-overlay">
          <div className="loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="main_container_section">
      <ShipdayCard
        initialConfig={integrations.shipday}
        subscribedZips={subscribedZips}
      />
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

function ShipdayCard({ initialConfig, subscribedZips }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Config state
  const [enabled, setEnabled] = useState(false);
  const [connected, setConnected] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyFirst4, setApiKeyFirst4] = useState(null);
  const [editingKey, setEditingKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [selectedZips, setSelectedZips] = useState([]);

  useEffect(() => {
    if (initialConfig) {
      applyConfig(initialConfig);
      setEditingKey(!initialConfig.hasApiKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig]);

  function applyConfig(d) {
    setEnabled(Boolean(d.enabled));
    setConnected(Boolean(d.connected));
    setHasApiKey(Boolean(d.hasApiKey));
    setApiKeyFirst4(d.apiKeyFirst4 || null);
    setSelectedZips(Array.isArray(d.zips) ? d.zips : []);
  }

  const zipOptions = subscribedZips.map(z => ({ label: z, value: z }));
  const selectedZipOptions = selectedZips.map(z => ({ label: z, value: z }));

  // Delete the stored key: clear the field into an editable state. Nothing is
  // sent to the server until the operator saves — saving with the field empty
  // removes the key (disconnects), typing a new key and saving reconnects.
  function deleteKey() {
    setEditingKey(true);
    setApiKeyInput("");
  }

  // Revert an in-progress edit/delete back to the stored, connected key.
  function cancelEdit() {
    setEditingKey(false);
    setApiKeyInput("");
  }

  // Live re-check of the stored key against Shipday (catches a key that was
  // revoked/rotated on Shipday's side after it was saved). Does not persist —
  // the stored config is unchanged either way.
  async function testConnection() {
    setTesting(true);

    const res = await dispatch(verifyIntegration(IntegrationType.SHIPDAY));

    if (res.success && res.data) {
      if (res.data.connected) {
        toast.success("Connection to Shipday is working.");
      } else {
        toast.error(
          res.data.message ||
            "Could not connect to Shipday. The key may have been revoked."
        );
      }
    } else {
      toast.error(res.error || "Failed to test the connection.");
    }

    setTesting(false);
  }

  async function save() {
    setSaving(true);

    const payload = {
      enabled,
      zips: selectedZips
    };
    // While editing the key, always send it: a value connects a new key, an
    // empty string removes the stored one. When not editing, the key is left
    // untouched (we never hold the real key on the client).
    if (editingKey) {
      payload.apiKey = apiKeyInput.trim();
    }

    const res = await dispatch(saveIntegration(IntegrationType.SHIPDAY, payload));

    if (res.success && res.data) {
      applyConfig(res.data);
      setEditingKey(!res.data.hasApiKey);
      setApiKeyInput("");
      toast.success("Shipday configuration saved.");
    } else {
      toast.error(res.error || "Failed to save configuration.");
    }

    setSaving(false);
  }

  let footerLabel = "Save configuration";
  if (saving) footerLabel = "Saving…";
  else if (editingKey) {
    footerLabel = apiKeyInput.trim() ? "Connect" : hasApiKey ? "Save" : "Connect";
  }

  return (
    <div className="integration_card">
      {/* Header */}
      <div className="integration_card_header">
        <div className="integration_card_title_group">
          <h3 className="integration_card_title">Shipday</h3>
          <span
            className={`integrations_status_badge ${
              connected ? "connected" : "disconnected"
            }`}
          >
            {connected ? "Connected" : "Not connected"}
          </span>
        </div>
        {connected && (
          <div className="integrations_toggle_group">
            <span className="integrations_toggle_text">Enable Shipday</span>
            <Checkbox
              toggle
              checked={enabled}
              onChange={(_, data) => setEnabled(data.checked)}
            />
          </div>
        )}
      </div>

      <p className="integration_card_description">
        Connect your own Shipday account. Donations created in the covered zip
        codes will be sent to Shipday as a delivery order.
      </p>

      {/* API key */}
      <div className="loc_form_field_group">
        <label className="loc_form_field_label">API key</label>
        {hasApiKey && !editingKey ? (
          <div className="integrations_inline">
            <div className="integrations_input_wrapper">
              <input
                type="text"
                className="integrations_input"
                value={`${apiKeyFirst4 || ""}••••••••••••`}
                disabled
              />
              <button className="integrations_input_icon_btn" onClick={deleteKey} title="Delete key">
                <Icon name="trash alternate outline" />
              </button>
            </div>
            <Button disabled={testing} onClick={testConnection}>
              {testing ? "Testing…" : "Test connection"}
            </Button>
          </div>
        ) : (
          <div className="integrations_inline">
            <input
              type="password"
              className="integrations_input"
              value={apiKeyInput}
              onChange={e => setApiKeyInput(e.target.value)}
              placeholder="Enter your Shipday API key"
              autoComplete="off"
            />
            {hasApiKey && <Button onClick={cancelEdit}>Cancel</Button>}
          </div>
        )}
        <span
          className={`integrations_help_text ${
            connected && !editingKey ? "connected" : ""
          }`}
        >
          {connected && !editingKey
            ? "API key saved. Connected to Shipday. Delete it to disconnect or replace it."
            : hasApiKey
            ? "Enter a new key to reconnect, or save with the field empty to remove it and disconnect."
            : "Enter your API key and connect. The key is verified with Shipday."}
        </span>
      </div>

      {/* Zip coverage — only once the key is connected */}
      {connected && (
        <>
          <div className="integration_card_divider" />
          <h4 className="integration_card_subtitle">Zip coverage</h4>
          <p className="integration_card_description">
            Select which zip codes Shipday covers. Donations created in these
            zips are sent to your Shipday account as a delivery order.
          </p>

          <div className="loc_form_field_group">
            <label className="loc_form_field_label">
              Zips covered by Shipday
            </label>
            <div className="integrations_select">
              <Select
                isMulti
                options={zipOptions}
                value={selectedZipOptions}
                onChange={vals =>
                  setSelectedZips((vals || []).map(v => v.value))
                }
                placeholder="Add zip…"
                noOptionsMessage={() => "No subscribed zips available"}
              />
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="integration_card_footer">
        <Button disabled={saving} onClick={save}>
          {footerLabel}
        </Button>
      </div>
    </div>
  );
}
